// This is the client's API it uses for interacting with the `server`.

import { type PageType, type SectionMonitoringData } from "../components/server-types";
import type { SectionRole } from "../shared/types/roles";

import { SERVER_API_PATH } from "./api-constants";
import type {
  InstructorAssessmentDataForSectionType,
  InviteStatus,
  PageAssessmentStatusType,
  ProgressOverviewData,
  SectionAssessmentSummaryParams,
  StudentAssessmentDataForSectionType,
  SystemCheckResultsData,
  UserEngagementDataType,
  UserInviteStatusType,
} from "./api-data-types";
import { serverApi } from "./api-server-actions";
import { createCached } from "./cache";

import {
  FullAssessmentData,
  PageAssessmentSummaryData,
  SectionAssessmentSummaryData,
  SectionEnrollmentData,
  SectionOutcomeSummaryData,
  SectionScoresData,
  StudentOutcomeCompetencyData,
} from "api/api-reporting-types";
import { withApiErrorReporting } from "utils/api-errors";
import { getOrgIdFromCookie } from "utils/auth";

export async function serverGetPageAssessmentStatuses({
  pageIds,
  sectionId,
}: {
  pageIds?: string[];
  sectionId?: string;
}): Promise<PageAssessmentStatusType> {
  const params: { page_ids?: string[]; section_id?: string } = {};
  if (pageIds) {
    params.page_ids = pageIds;
  }

  if (sectionId) {
    params.section_id = sectionId;
  }

  return withApiErrorReporting("serverPageAssessmentStatus", async () => {
    const response = await serverApi.post<PageAssessmentStatusType>(
      `${SERVER_API_PATH}/material/pages/assessment_status/`,
      params,
    );

    return response.data;
  });
}

// Type for the server response format
type UserInviteStatusResponseType = Array<{
  user_id: string;
  invite_status: string;
}>;

export async function serverGetUserInviteStatuses(): Promise<UserInviteStatusType> {
  return withApiErrorReporting("serverUserInviteStatus", async () => {
    const response = await serverApi.get<UserInviteStatusResponseType>(
      `${SERVER_API_PATH}/orgs/org_users/invite_status/`,
    );

    // Convert array of objects to Record<string, InviteStatus>
    const statusesRecord: UserInviteStatusType = {};
    response.data.forEach((item) => {
      statusesRecord[item.user_id] = item.invite_status as InviteStatus;
    });

    return statusesRecord;
  });
}

export async function serverGetSectionAssessmentSummary(
  params: SectionAssessmentSummaryParams,
): Promise<SectionAssessmentSummaryData[]> {
  return withApiErrorReporting("serverGetSectionAssessmentSummary", async () => {
    const response = await serverApi.post<SectionAssessmentSummaryData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/section_summary/`,
      params,
    );

    return response.data;
  });
}

export async function serverGetPageAssessmentSummary(
  params: SectionAssessmentSummaryParams,
): Promise<PageAssessmentSummaryData[]> {
  return withApiErrorReporting("serverGetPageAssessmentSummary", async () => {
    const response = await serverApi.post<PageAssessmentSummaryData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/page_summary/`,
      params,
    );

    return response.data;
  });
}

export async function serverGetFullAssessmentData(
  params: SectionAssessmentSummaryParams,
): Promise<FullAssessmentData[]> {
  return withApiErrorReporting("serverGetFullAssessmentData", async () => {
    const response = await serverApi.post<FullAssessmentData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/full_report/`,
      params,
    );

    return response.data;
  });
}

export async function serverGetSectionEnrollmentSummary(params: {
  term_id: string;
  course_id?: string;
}): Promise<SectionEnrollmentData[]> {
  return withApiErrorReporting("serverGetSectionEnrollmentSummary", async () => {
    const response = await serverApi.post<SectionEnrollmentData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/section_enrollment_summary/`,
      params,
    );

    return response.data;
  });
}

export async function serverGetSectionOutcomeSummary(
  termId: string,
): Promise<SectionOutcomeSummaryData[]> {
  return withApiErrorReporting("serverGetSectionOutcomeSummary", async () => {
    const response = await serverApi.post<SectionOutcomeSummaryData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/section_outcome_summary/`,
      { term_id: termId },
    );

    return response.data;
  });
}

export async function serverGetStudentOutcomeCompetency(
  termId?: string,
): Promise<StudentOutcomeCompetencyData[]> {
  return withApiErrorReporting("serverGetStudentOutcomeCompetency", async () => {
    const response = await serverApi.post<StudentOutcomeCompetencyData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/student_outcome_competency/`,
      { term_id: termId },
    );

    return response.data;
  });
}

export async function serverGetSectionScores(
  termId: string,
  courseId: string | undefined,
): Promise<SectionScoresData[]> {
  return withApiErrorReporting("serverGetSectionScores", async () => {
    const response = await serverApi.post<SectionScoresData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/section_scores/`,
      { term_id: termId, course_id: courseId },
    );

    return response.data;
  });
}

// Returns engagement data for all 3 date ranges: 7 days, 30 days, and -1 (all time)
export const serverGetEngagementData = createCached(
  async (sectionId: string): Promise<Record<number, Record<string, UserEngagementDataType>>> => {
    return withApiErrorReporting("serverGetEngagementData", async () => {
      const response = await serverApi.post<Record<number, Record<string, UserEngagementDataType>>>(
        `${SERVER_API_PATH}/material/sections/${sectionId}/engagement/`,
        {},
      );

      return response.data;
    });
  },
  60 * 10, // 10 minutes
);

export async function serverGetServerTime(): Promise<{ server_time: number }> {
  return withApiErrorReporting("serverGetServerTime", async () => {
    const response = await serverApi.get<{ server_time: number }>("/api/v1/basebox/server-time/", {
      headers: {
        "Cache-Control": "no-store",
        Pragma: "no-cache",
      },
    });
    return response.data;
  });
}

export async function serverGetProgressOverviewData(): Promise<ProgressOverviewData> {
  return withApiErrorReporting("serverGetProgressOverviewData", async () => {
    const response = await serverApi.get<ProgressOverviewData>(
      `${SERVER_API_PATH}/material/outcome_assessments/progress_overview/`,
    );

    return response.data;
  });
}

// This should only be used for the monitoring dashboard. It retrieves nested section data across all orgs.
export async function serverGetSectionsForMonitoring(): Promise<SectionMonitoringData[]> {
  return withApiErrorReporting("serverGetSectionsForMonitoring", async () => {
    const response = await serverApi.get<SectionMonitoringData[]>(
      `${SERVER_API_PATH}/material/sections/for_monitoring/`,
    );
    return response.data;
  });
}

export async function serverGetInstructorAssessments() {
  const orgId = getOrgIdFromCookie();
  return withApiErrorReporting("serverGetInstructorAssessments", async () => {
    const response = await serverApi.get<InstructorAssessmentDataForSectionType[]>(
      `${SERVER_API_PATH}/orgs/orgs/${orgId}/instructor_assessments/`,
    );
    return response.data;
  });
}

export async function serverGetStudentAssessments() {
  const orgId = getOrgIdFromCookie();
  return withApiErrorReporting("serverGetStudentAssessments", async () => {
    const response = await serverApi.get<StudentAssessmentDataForSectionType[]>(
      `${SERVER_API_PATH}/orgs/orgs/${orgId}/student_assessments/`,
    );
    return response.data;
  });
}

export async function serverGetHomePlannerPages(userRole: SectionRole): Promise<PageType[]> {
  return withApiErrorReporting("serverGetHomePlannerPages", async () => {
    const response = await serverApi.get<PageType[]>(
      `${SERVER_API_PATH}/material/pages/home_planner/?user_role=${userRole}`,
    );

    return response.data;
  });
}

export async function serverGetSystemCheckResults(
  start_date: string,
): Promise<SystemCheckResultsData[]> {
  return withApiErrorReporting("serverGetSystemCheckResults", async () => {
    const response = await serverApi.get<SystemCheckResultsData[]>(
      `${SERVER_API_PATH}/system_check/check_results/`,
      {
        params: { created_at__gte: start_date },
      },
    );
    return response.data;
  });
}
